/**
 * Barre de filtres commune : périmètre, période, actualisation.
 * Elle reste affichée quel que soit l'état des données (erreur, 403 de
 * périmètre…) : on peut toujours changer de filtre pour en sortir.
 */
import React, { useMemo } from 'react';
import { Button, Card, Segmented, Select, Space, Tag, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ScopeType, ScopesResponse } from '../../../api/monitoring/v2';
import { INK, cardStyle } from '../monitoringTheme';
import PeriodPicker from './PeriodPicker';
import type { DateRange, PresetKey } from './periods';
import type { MonitoringFilters } from './useMonitoringFilters';
import { useFormat } from './format';

const { Text } = Typography;

export type MonitoringFilterBarProps = {
    scopes: ScopesResponse | null;
    filters: MonitoringFilters;
    preset: PresetKey | null;
    onScopeChange: (type: ScopeType, id?: number) => void;
    onPeriodChange: (range: DateRange, preset: PresetKey | null) => void;
    onRefresh: () => void;
    refreshing?: boolean;
};

type Entry = { id: number; name: string; students?: number; parent_name?: string | null };

const entriesOf = (scopes: ScopesResponse | null, type: ScopeType): Entry[] => {
    switch (type) {
        case 'school':
            return scopes?.schools ?? [];
        case 'parent':
            return scopes?.parents ?? [];
        case 'student':
            return scopes?.students ?? [];
        default:
            return [];
    }
};

const MonitoringFilterBar: React.FC<MonitoringFilterBarProps> = ({
    scopes,
    filters,
    preset,
    onScopeChange,
    onPeriodChange,
    onRefresh,
    refreshing,
}) => {
    const { t } = useTranslation();
    const f = useFormat();

    const scopeOptions = useMemo(() => {
        // « all » est proposé à tous : pour un non-admin, le serveur le rabat
        // sur sa branche (MON-03-C), d'où un libellé différent.
        const options: Array<{ label: string; value: ScopeType }> = [
            { label: t(scopes?.can_view_all ? 'monitoring.scope.all' : 'monitoring.scope.allMine'), value: 'all' },
        ];
        if (scopes?.schools.length) options.push({ label: t('monitoring.scope.school'), value: 'school' });
        if (scopes?.parents.length) options.push({ label: t('monitoring.scope.parent'), value: 'parent' });
        if (scopes?.students.length) options.push({ label: t('monitoring.scope.student'), value: 'student' });
        return options;
    }, [scopes, t]);

    const entries = entriesOf(scopes, filters.scopeType);

    return (
        <Card style={cardStyle} styles={{ body: { padding: '14px 16px' } }}>
            <Space size={12} wrap style={{ width: '100%', rowGap: 12 }}>
                {scopeOptions.length > 1 && (
                    <Segmented
                        value={filters.scopeType}
                        aria-label={t('monitoring.scope.label')}
                        onChange={(v) => {
                            const type = v as ScopeType;
                            // On présélectionne la première entrée : changer de maille
                            // ne laisse pas l'écran vide en attendant un second clic.
                            onScopeChange(type, type === 'all' ? undefined : entriesOf(scopes, type)[0]?.id);
                        }}
                        options={scopeOptions}
                    />
                )}
                {filters.scopeType !== 'all' && (
                    <Select
                        showSearch
                        style={{ minWidth: 240 }}
                        aria-label={t('monitoring.filters.selectScope')}
                        placeholder={t('monitoring.filters.selectScope')}
                        value={filters.scopeId}
                        onChange={(id: number) => onScopeChange(filters.scopeType, id)}
                        options={entries.map((e) => ({
                            value: e.id,
                            label: e.name,
                            search: `${e.name} ${e.id} ${e.parent_name ?? ''}`.toLowerCase(),
                            entry: e,
                        }))}
                        optionRender={(option) => (
                            <Space size={6}>
                                <span>{option.data.entry.name}</span>
                                {option.data.entry.students !== undefined && (
                                    <Tag bordered={false} style={{ fontSize: 11 }}>
                                        {t('monitoring.filters.studentsCount', { n: f.count(option.data.entry.students) })}
                                    </Tag>
                                )}
                                {option.data.entry.parent_name && (
                                    <Text style={{ fontSize: 11, color: INK.muted }}>{option.data.entry.parent_name}</Text>
                                )}
                            </Space>
                        )}
                        filterOption={(input, option) => (option?.search ?? '').includes(input.toLowerCase())}
                        notFoundContent={<Text type="secondary">{t('monitoring.filters.noScope')}</Text>}
                    />
                )}
                <PeriodPicker
                    value={{ from: filters.from, to: filters.to }}
                    preset={preset}
                    onChange={onPeriodChange}
                    allowFuture
                    today={scopes?.today ?? null}
                />
                <Button icon={<ReloadOutlined />} onClick={onRefresh} loading={refreshing}>
                    {t('monitoring.filters.refresh')}
                </Button>
            </Space>
            {filters.scopeType !== 'all' && filters.scopeId === undefined && (
                <Text style={{ display: 'block', marginTop: 10, fontSize: 12, color: INK.muted }}>
                    {t('monitoring.filters.pickOne')}
                </Text>
            )}
        </Card>
    );
};

export default MonitoringFilterBar;
